'use client';

import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import dynamic from 'next/dynamic';
import { useCadrinoCopy } from '@/lib/cadrino-landing/useCadrinoCopy';
import { useWebsitePagesCopy } from '@/lib/cadrino-landing/useWebsitePagesCopy';
import { useClientMounted } from '@/hooks/useClientMounted';
import { Container, Section, SectionHeader, MotionBlock, PrimaryButton } from './shared';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DashboardChrome } from './product/ProductMocks';
import { ErvAttendanceSimulationSkeleton } from './simulation/ErvAttendanceSimulationSkeleton';
import { cn } from '@/lib/utils';
const ErvAnalyticsSimulation = dynamic(() => import('./simulation/ErvAnalyticsSimulation').then(m => m.ErvAnalyticsSimulation), {
  ssr: false,
  loading: () => <div className="min-h-[420px] animate-pulse rounded-xl bg-gray-100" />
});
const ErvAttendanceSimulation = dynamic(() => import('./simulation/ErvAttendanceSimulation').then(m => m.ErvAttendanceSimulation), {
  ssr: false,
  loading: () => <ErvAttendanceSimulationSkeleton />
});
const ErvDevicesSimulation = dynamic(() => import('./simulation/ErvDevicesSimulation').then(m => m.ErvDevicesSimulation), {
  ssr: false,
  loading: () => <div className="min-h-[380px] animate-pulse rounded-xl bg-gray-100" />
});
type ShowcaseTab = 'analytics' | 'attendance' | 'devices';
export function ProductShowcase() {
  const $ = _c(26);
  const c = useCadrinoCopy();
  const pages = useWebsitePagesCopy();
  const [tab, setTab] = useState("analytics");
  const mounted = useClientMounted();
  let t0;
  if ($[0] !== c.product.tabs.analytics) {
    t0 = {
      value: "analytics",
      label: c.product.tabs.analytics
    };
    $[0] = c.product.tabs.analytics;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== c.product.tabs.attendance) {
    t1 = {
      value: "attendance",
      label: c.product.tabs.attendance
    };
    $[2] = c.product.tabs.attendance;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== c.product.tabs.devices) {
    t2 = {
      value: "devices",
      label: c.product.tabs.devices
    };
    $[4] = c.product.tabs.devices;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t0 || $[7] !== t1 || $[8] !== t2) {
    t3 = [t0, t1, t2];
    $[6] = t0;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const tabs = t3;
  let t4;
  if ($[10] !== c.product.subtitle || $[11] !== c.product.title) {
    t4 = <MotionBlock><SectionHeader title={c.product.title} subtitle={c.product.subtitle} /></MotionBlock>;
    $[10] = c.product.subtitle;
    $[11] = c.product.title;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== c.product.windowTitles.analytics || $[14] !== c.product.windowTitles.attendance || $[15] !== c.product.windowTitles.devices || $[16] !== mounted || $[17] !== tab || $[18] !== tabs) {
    t5 = <MotionBlock className="mt-12">{!mounted ? <div className="w-full"><div className="mb-6 flex h-auto w-full justify-start gap-1 rounded-xl border border-white/[0.1] bg-cadrino-surface p-1.5">{tabs.map(_temp)}</div><DashboardChrome title={c.product.windowTitles.analytics}><div className="-m-4 min-h-[420px] animate-pulse rounded-xl bg-gray-100" /></DashboardChrome></div> : <Tabs value={tab} onValueChange={v => setTab(v as ShowcaseTab)} className="w-full"><TabsList className="mb-6 h-auto w-full justify-start gap-1 rounded-xl border border-white/[0.1] bg-cadrino-surface p-1.5 text-slate-400">{tabs.map(_temp2)}</TabsList><TabsContent value="analytics" className="mt-0" forceMount={true}><div className={cn(tab !== "analytics" && "hidden")}><DashboardChrome title={c.product.windowTitles.analytics}><div className="-m-4"><ErvAnalyticsSimulation active={tab === "analytics"} /></div></DashboardChrome></div></TabsContent><TabsContent value="attendance" className="mt-0" forceMount={true}><div className={cn(tab !== "attendance" && "hidden")}><DashboardChrome title={c.product.windowTitles.attendance}><div className="-m-4"><ErvAttendanceSimulation active={tab === "attendance"} /></div></DashboardChrome></div></TabsContent><TabsContent value="devices" className="mt-0" forceMount={true}><div className={cn(tab !== "devices" && "hidden")}><DashboardChrome title={c.product.windowTitles.devices}><div className="-m-4"><ErvDevicesSimulation active={tab === "devices"} /></div></DashboardChrome></div></TabsContent></Tabs>}</MotionBlock>;
    $[13] = c.product.windowTitles.analytics;
    $[14] = c.product.windowTitles.attendance;
    $[15] = c.product.windowTitles.devices;
    $[16] = mounted;
    $[17] = tab;
    $[18] = tabs;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== pages.nav.product) {
    t6 = <MotionBlock className="mt-8 flex flex-wrap gap-3"><PrimaryButton href="/product">{pages.nav.product}</PrimaryButton></MotionBlock>;
    $[20] = pages.nav.product;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== t4 || $[23] !== t5 || $[24] !== t6) {
    t7 = <Section id="product" className="bg-cadrino-surface/30"><Container>{t4}{t5}{t6}</Container></Section>;
    $[22] = t4;
    $[23] = t5;
    $[24] = t6;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  return t7;
}
function _temp2(t0) {
  const {
    value: value_0,
    label: label_0
  } = t0;
  return <TabsTrigger key={value_0} value={value_0} className={cn("flex-1 rounded-lg border border-transparent px-3 py-2.5 text-sm font-medium shadow-none transition-all duration-200 sm:px-4", "text-slate-400 hover:bg-white/[0.04] hover:text-slate-200", "data-[state=active]:border-white/[0.1] data-[state=active]:bg-white/[0.1]", "data-[state=active]:text-slate-50 data-[state=active]:shadow-none", "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cadrino-primary/50")}>{label_0}</TabsTrigger>;
}
function _temp(t0) {
  const {
    value,
    label
  } = t0;
  return <div key={value} className={cn("flex-1 rounded-lg px-3 py-2.5 text-center text-sm font-medium sm:px-4", value === "analytics" ? "border border-white/[0.1] bg-white/[0.1] text-slate-50" : "text-slate-400")}>{label}</div>;
}